import { useMemo, useState } from 'react';
import { IconOk } from '../Icons';
import {
  DataTable, EmptyState, StatusBadge, type Spalte,
} from '../muster';

/**
 * Die Gerätetafel — das Muster der Heizung, für alle Gewerke.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM EIN GEMEINSAMER BAUSTEIN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Heizung hat die Form vorgegeben, und sie ist gut: eine flache Tabelle,
 * Suche über alles, Filter nach Art, Fabrikat und Typ in einer Spalte, Raum
 * daneben, Detailverweis am Ende.
 *
 * Sie ein zweites und drittes Mal abzuschreiben hieße, drei Tabellen zu
 * pflegen, die gleich aussehen sollen. Beim ersten Umbau sehen sie es nicht
 * mehr — und keiner merkt es, weil niemand zwei Gewerke nebeneinander
 * öffnet.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM FABRIKAT UND TYP IN EINER SPALTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Steht ein Fabrikat da, ist es die Angabe, die zählt — dann tritt der
 * Modellname zurück. Steht keines da, ist der Modellname alles, was es gibt,
 * und er wird blass gesetzt: Er ist kein Fabrikat, er sieht nur an dieser
 * Stelle so aus.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * UMZUG AUF `DataTable` (Block U, Paket U6b)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Vorher: `<table className="tabelle">` von Hand, `gefiltert.slice(0, 300)`
 * und darunter der Satz „Nur die ersten 300 gezeigt". Diese Kappung entfällt
 * ersatzlos — `DataTable` virtualisiert ab 200 Zeilen und zeigt trotzdem
 * alle. Suche und Trefferzahl kommen aus dem Muster; der Artenfilter bleibt
 * hier, weil er die Stückzahl je Art in der Auswahl nennt.
 */

export interface Geraetezeile {
  id: string;
  art: string;
  bezeichnung: string | null;
  hersteller?: string | null;
  modell?: string | null;
  familie?: string | null;
  typ?: string | null;
  kennung?: string | null;
  raum?: string | null;
  geschoss?: string | null;

  /** Was rechts als Kennwert steht — je Gewerk verschieden. */
  kennwert?: string | null;

  /** Der öffentliche Verweis, oder null wenn kein Schlüssel hinterlegt ist. */
  verweis?: string | null;

  /** Was noch fehlt. Wird als blasse Zeile darunter gezeigt. */
  offen?: string[] | null;
}

export function Geraetetafel({ zeilen, kennwertName, aufDetail, kennwertEinheit }: {
  zeilen: Geraetezeile[];
  kennwertName: string;
  /** Einheit der Kennwertspalte, sofern die Zeilen sie nicht selbst tragen. */
  kennwertEinheit?: string;
  aufDetail?: (id: string) => void;
}) {
  const [art, setArt] = useState('');
  const [kopiert, setKopiert] = useState<string | null>(null);

  const arten = useMemo(() => {
    const m = new Map<string, number>();
    for (const z of zeilen) m.set(z.art, (m.get(z.art) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [zeilen]);

  const gefiltert = useMemo(
    () => (art ? zeilen.filter((z) => z.art === art) : zeilen),
    [zeilen, art]);

  const spalten: Spalte<Geraetezeile>[] = useMemo(() => [
    {
      schluessel: 'art', titel: 'Art', breite: '150px',
      wert: (z) => z.art,
      inhalt: (z) => <span className="schwach">{z.art}</span>,
    },
    {
      schluessel: 'geraet', titel: 'Fabrikat und Typ', immerSichtbar: true,
      // Der Suchwert trägt alles, was in der Zelle steht — sonst findet die
      // Suche des Musters den Modellnamen nicht, der nur blass darunter hängt.
      wert: (z) => [z.hersteller, z.modell, z.bezeichnung, z.familie, z.typ]
        .filter(Boolean).join(' '),
      inhalt: (z) => (
        <>
          {z.hersteller || z.modell ? (
            <strong>
              {[z.hersteller, z.modell].filter(Boolean).join(' ')}
            </strong>
          ) : (
            <>
              <strong>{z.bezeichnung ?? '—'}</strong>
              {(z.familie || z.typ) && (
                <div className="schwach" style={{ fontSize: 11 }}>
                  {[z.familie, z.typ].filter(Boolean).join(' · ')}
                </div>
              )}
            </>
          )}

          {/* Was fehlt, steht unter der Zeile und nicht in einer eigenen
              Spalte: Eine Spalte, die bei neun von zehn Zeilen leer ist,
              kostet Breite und sagt nichts. Als `StatusBadge`, damit die
              Aussage nicht allein an der Farbe hängt (Abschnitt 18). */}
          {z.offen && z.offen.length > 0 && (
            <div style={{ marginTop: 2 }}>
              <StatusBadge zustand="warnung" text={z.offen.join(' · ')} klein />
            </div>
          )}
        </>
      ),
    },
    {
      schluessel: 'kennung', titel: 'Kennung', breite: '140px',
      wert: (z) => z.kennung,
      inhalt: (z) => <span className="schwach">{z.kennung ?? '—'}</span>,
    },
    {
      schluessel: 'ort', titel: 'Raum', breite: '180px',
      wert: (z) => [z.geschoss, z.raum].filter(Boolean).join(' · '),
      inhalt: (z) => (
        <span className="schwach">
          {[z.geschoss, z.raum].filter(Boolean).join(' · ') || '—'}
        </span>
      ),
    },
    {
      schluessel: 'kennwert', titel: kennwertName, ausrichtung: 'rechts',
      einheit: kennwertEinheit, breite: '150px',
      wert: (z) => z.kennwert,
    },
    {
      schluessel: 'aktionen', titel: '', sortierbar: false, breite: '150px',
      // Zwei Knoepfe, ein Abstand: Die Klasse 'klein' hat in styles.css
      // KEINE Regel - sie war nie eine -, und die 4 px Aussenabstand des
      // zweiten Knopfes reichten nicht. Auf dem Bildschirm des Fachplaners
      // stand am 23.09.2026 ein Knopf namens "DetailsLink". Der Abstand
      // kommt jetzt aus einem inline-flex mit gap und nicht aus einem
      // Aussenabstand, der beim naechsten Knopf wieder fehlt.
      inhalt: (z) => (
        <span style={{ display: 'inline-flex', gap: 6, whiteSpace: 'nowrap' }}>
          {aufDetail && (
            <button className="klein" onClick={() => aufDetail(z.id)}>
              Details
            </button>
          )}

          {z.verweis && (
            <button
              className="klein"
              title={z.verweis}
              onClick={() => {
                navigator.clipboard.writeText(z.verweis!);
                setKopiert(z.id);
                setTimeout(() => setKopiert(null), 1500);
              }}
            >
              {kopiert === z.id
                ? <><IconOk size={12} /> kopiert</>
                : 'Link kopieren'}
            </button>
          )}
        </span>
      ),
    },
  ], [kennwertName, kennwertEinheit, aufDetail, kopiert]);

  return (
    <DataTable
      name="Geräte"
      spalten={spalten}
      zeilen={gefiltert}
      schluessel={(z) => z.id}
      suchePlatzhalter="Fabrikat, Kennung, Raum …"
      ausfuhr
      ausfuhrDateiname="geraete"
      werkzeuge={(
        <select
          value={art}
          onChange={(e) => setArt(e.target.value)}
          aria-label="Geräteart"
          style={{ width: 'auto' }}
        >
          <option value="">alle Arten ({zeilen.length})</option>
          {arten.map(([a, n]) => (
            <option key={a} value={a}>{a} ({n})</option>
          ))}
        </select>
      )}
      leer={(
        <EmptyState
          titel={art
            ? `Keine Zeile der Art „${art}“.`
            : 'Für dieses Modell ist noch kein Gerät aufgenommen.'}
          erklaerung={art
            ? 'Der Artenfilter schränkt die Liste ein. Ohne ihn stehen alle Geräte des Gewerks da.'
            : 'Die Geräte entstehen aus einer Modellaufnahme des Gewerks.'}
          aktion={art
            ? (
              <button className="m-knopf" onClick={() => setArt('')}>
                Artenfilter aufheben
              </button>
            )
            : undefined}
        />
      )}
    />
  );
}
